import React, { useState, useEffect } from 'react';
import { Search, Calendar, TrendingUp, MessageSquare, BarChart3, PieChart, ClipboardList } from 'lucide-react';
import { getDashboardAnalytics } from '../services/api';
import { PieChart as RechartsPieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';

interface DashboardData {
    total_mentions: number;
    workload_remaining: number;
    workload_sentiment: {
        positive: number;
        negative: number;
        neutral: number;
    };
    my_workload_remaining: number;
    my_workload_sentiment: {
        positive: number;
        negative: number;
        neutral: number;
    };
    quality_related_mentions: number;
    average_sentiment: string;
    quality_complaints: Array<{ name: string; count: number }>;
    total_quality_complaints: number;
    sentiment_analysis: {
        positive: { count: number; percentage: number };
        negative: { count: number; percentage: number };
        neutral: { count: number; percentage: number };
    };
    platform_breakdown: {
        [key: string]: {
            positive: { count: number; percentage: number };
            negative: { count: number; percentage: number };
            neutral: { count: number; percentage: number };
        };
    };
    date_range: {
        start: string;
        end: string;
    };
}

const Dashboard: React.FC = () => {
    const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [platform, setPlatform] = useState(''); // Empty string means 'All'

    // Get today's date in local time (YYYY-MM-DD)
    const getLocalDateString = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const getDefaultDate = () => getLocalDateString(new Date());

    const [startDate, setStartDate] = useState(getDefaultDate());
    const [endDate, setEndDate] = useState(getDefaultDate());

    useEffect(() => {
        fetchDashboardData();
    }, [startDate, endDate, platform]);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            const params: any = {
                timezone_offset: new Date().getTimezoneOffset(),
            };
            if (searchQuery) params.search_query = searchQuery;
            if (startDate) params.start_date = startDate;
            if (endDate) params.end_date = endDate;
            if (platform) params.platform = platform;

            const data = await getDashboardAnalytics(params);
            setDashboardData(data);
        } catch (error) {
            console.error('Failed to fetch dashboard data', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => {
        fetchDashboardData();
    };

    const COLORS = {
        positive: '#4CAF50',
        negative: '#F44336',
        neutral: '#FFC107'
    };

    if (loading || !dashboardData) {
        return (
            <div className="page-container">
                <div className="loading-state">Loading dashboard...</div>
            </div>
        );
    }

    // Prepare data for sentiment pie chart
    const sentimentPieData = [
        { name: 'Positive', value: dashboardData.sentiment_analysis.positive.count, color: COLORS.positive },
        { name: 'Negative', value: dashboardData.sentiment_analysis.negative.count, color: COLORS.negative },
        { name: 'Neutral', value: dashboardData.sentiment_analysis.neutral.count, color: COLORS.neutral }
    ];

    return (
        <div className="page-container">
            {/* Header with Search */}
            <div className="dashboard-header" style={{ marginBottom: '2rem' }}>
                <div>
                    <h1>Dashboard</h1>
                    <p className="page-subtitle">Analytics and insights from your social media</p>
                </div>

                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                    <select
                        value={platform}
                        onChange={(e) => setPlatform(e.target.value)}
                        style={{
                            padding: '0.5rem',
                            border: '1px solid var(--border-color)',
                            borderRadius: '8px',
                            fontSize: '0.875rem',
                            backgroundColor: 'white',
                            minWidth: '120px'
                        }}
                    >
                        <option value="">All Platforms</option>
                        <option value="INSTAGRAM">Instagram</option>
                        <option value="FACEBOOK">Facebook</option>
                        <option value="TIKTOK">TikTok</option>
                        <option value="TWITTER">X</option>
                    </select>

                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            style={{
                                padding: '0.5rem',
                                border: '1px solid var(--border-color)',
                                borderRadius: '8px',
                                fontSize: '0.875rem'
                            }}
                        />
                        <span style={{ color: 'var(--text-secondary)' }}>→</span>
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            style={{
                                padding: '0.5rem',
                                border: '1px solid var(--border-color)',
                                borderRadius: '8px',
                                fontSize: '0.875rem'
                            }}
                        />
                    </div>
                </div>
            </div>

            {/* Metric Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
                {/* My Workload */}
                <div className="metric-card" style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    padding: '1.25rem'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '48px',
                        height: '48px',
                        borderRadius: '12px',
                        background: '#FFF8E1',
                        flexShrink: 0
                    }}>
                        <ClipboardList size={24} color="#F9A825" strokeWidth={2} />
                    </div>

                    <div style={{ flex: 1 }}>
                        <div style={{
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            letterSpacing: '0.5px',
                            color: 'var(--text-secondary)',
                            marginBottom: '0.25rem',
                            textTransform: 'uppercase'
                        }}>
                            My Workload
                        </div>

                        <div style={{
                            fontSize: '1.75rem',
                            fontWeight: '700',
                            color: 'var(--text-primary)',
                            letterSpacing: '-0.01em',
                            fontFamily: 'Inter, sans-serif',
                            lineHeight: '1',
                            marginBottom: '0.75rem'
                        }}>
                            {(dashboardData.my_workload_remaining ?? 0).toLocaleString()}
                        </div>

                        {/* Sentiment Breakdown */}
                        {dashboardData.my_workload_sentiment && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.75rem' }}>
                                    <div style={{
                                        width: '16px',
                                        height: '16px',
                                        borderRadius: '50%',
                                        backgroundColor: COLORS.positive,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '10px'
                                    }}>😊</div>
                                    <span style={{ color: COLORS.positive, fontWeight: '500' }}>Positive: {dashboardData.my_workload_sentiment.positive || 0}</span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.75rem' }}>
                                    <div style={{
                                        width: '16px',
                                        height: '16px',
                                        borderRadius: '50%',
                                        backgroundColor: COLORS.neutral,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '10px'
                                    }}>😐</div>
                                    <span style={{ color: COLORS.neutral, fontWeight: '500' }}>Neutral: {dashboardData.my_workload_sentiment.neutral || 0}</span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.75rem' }}>
                                    <div style={{
                                        width: '16px',
                                        height: '16px',
                                        borderRadius: '50%',
                                        backgroundColor: COLORS.negative,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '10px'
                                    }}>😞</div>
                                    <span style={{ color: COLORS.negative, fontWeight: '500' }}>Negative: {dashboardData.my_workload_sentiment.negative || 0}</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Total Workload Remaining */}
                <div className="metric-card" style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    padding: '1.25rem'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '48px',
                        height: '48px',
                        borderRadius: '12px',
                        background: '#FFF8E1',
                        flexShrink: 0
                    }}>
                        <ClipboardList size={24} color="#F9A825" strokeWidth={2} />
                    </div>

                    <div style={{ flex: 1 }}>
                        <div style={{
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            letterSpacing: '0.5px',
                            color: 'var(--text-secondary)',
                            marginBottom: '0.25rem',
                            textTransform: 'uppercase'
                        }}>
                            Total Workload Remaining
                        </div>

                        <div style={{
                            fontSize: '1.75rem',
                            fontWeight: '700',
                            color: 'var(--text-primary)',
                            letterSpacing: '-0.01em',
                            fontFamily: 'Inter, sans-serif',
                            lineHeight: '1',
                            marginBottom: '0.75rem'
                        }}>
                            {(dashboardData.workload_remaining ?? 0).toLocaleString()}
                        </div>

                        {/* Sentiment Breakdown */}
                        {dashboardData.workload_sentiment && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.75rem' }}>
                                    <div style={{
                                        width: '16px',
                                        height: '16px',
                                        borderRadius: '50%',
                                        backgroundColor: COLORS.positive,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '10px'
                                    }}>😊</div>
                                    <span style={{ color: COLORS.positive, fontWeight: '500' }}>Positive: {dashboardData.workload_sentiment.positive || 0}</span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.75rem' }}>
                                    <div style={{
                                        width: '16px',
                                        height: '16px',
                                        borderRadius: '50%',
                                        backgroundColor: COLORS.neutral,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '10px'
                                    }}>😐</div>
                                    <span style={{ color: COLORS.neutral, fontWeight: '500' }}>Neutral: {dashboardData.workload_sentiment.neutral || 0}</span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.75rem' }}>
                                    <div style={{
                                        width: '16px',
                                        height: '16px',
                                        borderRadius: '50%',
                                        backgroundColor: COLORS.negative,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '10px'
                                    }}>😞</div>
                                    <span style={{ color: COLORS.negative, fontWeight: '500' }}>Negative: {dashboardData.workload_sentiment.negative || 0}</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Total Mentions & Average Sentiment */}
                <div className="metric-card" style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1.25rem',
                    padding: '1.25rem'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '48px',
                            height: '48px',
                            borderRadius: '12px',
                            background: '#E3F2FD',
                            flexShrink: 0
                        }}>
                            <MessageSquare size={24} color="#2196F3" strokeWidth={2} />
                        </div>

                        <div style={{ flex: 1 }}>
                            <div style={{
                                fontSize: '0.75rem',
                                fontWeight: '600',
                                letterSpacing: '0.5px',
                                color: 'var(--text-secondary)',
                                marginBottom: '0.25rem',
                                textTransform: 'uppercase'
                            }}>
                                Total Mentions
                            </div>

                            <div style={{
                                fontSize: '1.75rem',
                                fontWeight: '700',
                                color: 'var(--text-primary)',
                                letterSpacing: '-0.01em',
                                fontFamily: 'Inter, sans-serif',
                                lineHeight: '1'
                            }}>
                                {dashboardData.total_mentions.toLocaleString()}
                            </div>
                        </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '48px',
                            height: '48px',
                            borderRadius: '12px',
                            background: dashboardData.average_sentiment === 'Positive' ? '#E8F5E9' :
                                dashboardData.average_sentiment === 'Negative' ? '#FFEBEE' : '#FFF8E1',
                            flexShrink: 0
                        }}>
                            <TrendingUp size={24} color={
                                dashboardData.average_sentiment === 'Positive' ? COLORS.positive :
                                    dashboardData.average_sentiment === 'Negative' ? COLORS.negative : COLORS.neutral
                            } strokeWidth={2} />
                        </div>

                        <div style={{ flex: 1 }}>
                            <div style={{
                                fontSize: '0.75rem',
                                fontWeight: '600',
                                letterSpacing: '0.5px',
                                color: 'var(--text-secondary)',
                                marginBottom: '0.25rem',
                                textTransform: 'uppercase'
                            }}>
                                Average Sentiment
                            </div>

                            <div style={{
                                fontSize: '1.75rem',
                                fontWeight: '700',
                                color: dashboardData.average_sentiment === 'Positive' ? COLORS.positive :
                                    dashboardData.average_sentiment === 'Negative' ? COLORS.negative : COLORS.neutral,
                                letterSpacing: '-0.01em',
                                fontFamily: 'Inter, sans-serif',
                                lineHeight: '1'
                            }}>
                                {dashboardData.average_sentiment}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Charts Section */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '2rem' }}>
                {/* Sentiment and Platform Analysis */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
                    {/* Overall Sentiment Analysis */}
                    <div className="chart-card">
                        <div className="chart-header">
                            <h3>OVERALL SENTIMENT ANALYSIS</h3>
                            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                                Breakdown of sentiment across mentions
                            </p>
                        </div>

                        <div style={{ marginTop: '1.5rem' }}>
                            {[
                                { key: 'positive', label: 'Positive', color: COLORS.positive, data: dashboardData.sentiment_analysis.positive },
                                { key: 'neutral', label: 'Neutral', color: COLORS.neutral, data: dashboardData.sentiment_analysis.neutral },
                                { key: 'negative', label: 'Negative', color: COLORS.negative, data: dashboardData.sentiment_analysis.negative }
                            ].map(item => (
                                <div key={item.key} style={{ marginBottom: '1rem' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                                        <span style={{ fontWeight: '500' }}>{item.label}</span>
                                        <span style={{ color: 'var(--text-secondary)' }}>
                                            {item.data.percentage}% | {item.data.count.toLocaleString()}
                                        </span>
                                    </div>
                                    <div style={{
                                        width: '100%',
                                        height: '8px',
                                        backgroundColor: '#f0f0f0',
                                        borderRadius: '4px',
                                        overflow: 'hidden'
                                    }}>
                                        <div style={{
                                            width: `${item.data.percentage}%`,
                                            height: '100%',
                                            backgroundColor: item.color,
                                            transition: 'width 0.3s ease'
                                        }} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Platform Sentiment Breakdown */}
                    <div className="chart-card">
                        <div className="chart-header">
                            <h3>PLATFORM SENTIMENT BREAKDOWN</h3>
                            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                                Breakdown of across social platforms
                            </p>
                        </div>

                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                            gap: '1.5rem',
                            marginTop: '1.5rem'
                        }}>
                            {Object.entries(dashboardData.platform_breakdown).map(([platform, sentiment]) => {
                                const pieData = [
                                    { name: 'Positive', value: sentiment.positive.percentage, color: COLORS.positive },
                                    { name: 'Negative', value: sentiment.negative.percentage, color: COLORS.negative },
                                    { name: 'Neutral', value: sentiment.neutral.percentage, color: COLORS.neutral }
                                ];

                                return (
                                    <div key={platform} style={{ textAlign: 'center' }}>
                                        <ResponsiveContainer width="100%" height={120}>
                                            <RechartsPieChart>
                                                <Pie
                                                    data={pieData}
                                                    cx="50%"
                                                    cy="50%"
                                                    innerRadius={30}
                                                    outerRadius={50}
                                                    paddingAngle={2}
                                                    dataKey="value"
                                                >
                                                    {pieData.map((entry, index) => (
                                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                                    ))}
                                                </Pie>
                                                <Tooltip />
                                            </RechartsPieChart>
                                        </ResponsiveContainer>
                                        <div style={{ fontWeight: '600', marginTop: '0.5rem', textTransform: 'capitalize' }}>
                                            {platform.toLowerCase() === 'twitter' ? 'X' : platform}
                                        </div>
                                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                                            <span style={{ color: COLORS.positive }}>
                                                {sentiment.positive.percentage}%
                                            </span>
                                            {' / '}
                                            <span style={{ color: COLORS.neutral }}>
                                                {sentiment.neutral.percentage}%
                                            </span>
                                            {' / '}
                                            <span style={{ color: COLORS.negative }}>
                                                {sentiment.negative.percentage}%
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
